/**
 * Therapist Pre-Session Workflow page (Spec H).
 *
 * Lets the therapist:
 *   - Browse / create / edit pre-session templates (a named bundle of tasks).
 *   - Assign a template to a specific client with a due date.
 *   - See which assignments are open and their current task-completion status.
 *
 * Replaces the curl workflow described in the dev test guide Phase 3.
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Stack, Button, Chip,
  LinearProgress, Alert, Snackbar, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, IconButton, Tooltip, Divider,
  List, ListItem, ListItemText, FormControlLabel, Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AssignmentIcon from '@mui/icons-material/AssignmentInd';
import PersonAddIcon from '@mui/icons-material/PersonAddAlt1';
import { rawApiClient as apiClient } from '../../services/api';
import InviteClientDialog from '../../components/therapist/InviteClientDialog';

// Must mirror server/models/PreSessionTemplate.js TaskSchema enum exactly.
const TASK_TYPES = [
  { value: 'assessment', label: 'Assessment (PHQ-9, GAD-7, etc.)' },
  { value: 'reading', label: 'Reading / educational material' },
  { value: 'acknowledgement', label: 'Acknowledgement (consent / read & confirm)' }
];

// Fallback used when the resolver hasn't returned (or empty result).
const FALLBACK_ASSESSMENT_KEYS = ['phq9', 'gad7'];

const emptyTemplate = () => ({
  name: '',
  description: '',
  gateSessionStart: true,
  tasks: [{ type: 'assessment', title: 'PHQ-9', required: true, assessmentKey: 'phq9' }]
});

export default function TherapistPreSession() {
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [assessmentKeys, setAssessmentKeys] = useState(FALLBACK_ASSESSMENT_KEYS);
  const [assessmentLabels, setAssessmentLabels] = useState({}); // { phq9: 'PHQ-9 — Patient Health Questionnaire-9' }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [working, setWorking] = useState(false);

  // Create / edit template
  const [tplDialog, setTplDialog] = useState({ open: false, draft: emptyTemplate(), id: null });

  // Assign template
  const [assignDialog, setAssignDialog] = useState({ open: false, templateId: null, clientId: '', dueAt: '' });

  // Invite client dialog (separate flow from template assignment)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  // Per-client assignment list (displayed inline as expandable rows)
  const [assignmentsByClient, setAssignmentsByClient] = useState({});

  async function load() {
    setLoading(true);
    try {
      const [tpls, clientsRes, available] = await Promise.all([
        apiClient.get('/pre-session/templates'),
        apiClient.get('/therapist/clients').catch(() => ({ data: { clients: [] } })),
        apiClient.get('/assessment-governance/therapist/available').catch(() => ({ data: { testDefinitions: [] } }))
      ]);
      setTemplates(tpls.data?.templates || tpls.data || []);
      const clientList = clientsRes.data?.clients || clientsRes.data || [];
      setClients(Array.isArray(clientList) ? clientList : []);
      const defs = available.data?.testDefinitions || [];
      if (defs.length > 0) {
        setAssessmentKeys(defs.map(d => d.testKey));
        const labels = {};
        defs.forEach(d => {
          labels[d.testKey] = d.shortName ? `${d.shortName}` : (d.fullName || d.testKey);
        });
        setAssessmentLabels(labels);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not load pre-session data');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function loadAssignmentsForClient(clientId) {
    try {
      const res = await apiClient.get(`/pre-session/assignments/client/${clientId}`);
      setAssignmentsByClient((prev) => ({ ...prev, [clientId]: res.data?.assignments || res.data || [] }));
    } catch (err) {
      setError(err?.response?.data?.error || `Could not load assignments for client`);
    }
  }

  async function saveTemplate() {
    setWorking(true);
    try {
      const body = {
        name: tplDialog.draft.name,
        description: tplDialog.draft.description,
        gateSessionStart: tplDialog.draft.gateSessionStart,
        tasks: tplDialog.draft.tasks.map((t, idx) => ({
          type: t.type,
          title: t.title,
          description: t.description || '',
          assessmentKey: t.type === 'assessment' ? (t.assessmentKey || null) : null,
          readingContent: t.type === 'reading' ? (t.readingContent || '') : '',
          required: !!t.required,
          order: idx
        }))
      };
      if (tplDialog.id) {
        await apiClient.patch(`/pre-session/templates/${tplDialog.id}`, body);
        setToast('Template updated');
      } else {
        await apiClient.post('/pre-session/templates', body);
        setToast('Template created');
      }
      setTplDialog({ open: false, draft: emptyTemplate(), id: null });
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not save template');
    } finally {
      setWorking(false);
    }
  }

  async function deleteTemplate(id) {
    if (!window.confirm('Delete this template? Active assignments are preserved.')) return;
    try {
      await apiClient.delete(`/pre-session/templates/${id}`);
      setToast('Template deleted');
      await load();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not delete template');
    }
  }

  async function submitAssignment() {
    if (!assignDialog.clientId || !assignDialog.templateId) {
      setError('Pick a client and a template');
      return;
    }
    setWorking(true);
    try {
      await apiClient.post('/pre-session/assignments', {
        clientId: assignDialog.clientId,
        templateId: assignDialog.templateId,
        dueAt: assignDialog.dueAt || undefined
      });
      setToast('Assigned to client');
      setAssignDialog({ open: false, templateId: null, clientId: '', dueAt: '' });
      // Refresh the relevant client's assignment list if expanded
      if (assignmentsByClient[assignDialog.clientId]) {
        await loadAssignmentsForClient(assignDialog.clientId);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Could not create assignment');
    } finally {
      setWorking(false);
    }
  }

  function openCreate() {
    setTplDialog({ open: true, draft: emptyTemplate(), id: null });
  }
  function openEdit(tpl) {
    setTplDialog({
      open: true,
      id: tpl._id,
      draft: {
        name: tpl.name || '',
        description: tpl.description || '',
        gateSessionStart: tpl.gateSessionStart !== false,
        tasks: tpl.tasks?.length ? tpl.tasks : [{ type: 'assessment', title: 'PHQ-9', required: true, assessmentKey: 'phq9' }]
      }
    });
  }
  function openAssign(templateId) {
    const dueDefault = new Date(Date.now() + 24 * 3600 * 1000).toISOString().slice(0, 16);
    setAssignDialog({ open: true, templateId, clientId: '', dueAt: dueDefault });
  }

  function updateDraftTask(idx, patch) {
    setTplDialog((prev) => {
      const tasks = [...prev.draft.tasks];
      tasks[idx] = { ...tasks[idx], ...patch };
      return { ...prev, draft: { ...prev.draft, tasks } };
    });
  }
  function addDraftTask() {
    setTplDialog((prev) => ({
      ...prev,
      draft: {
        ...prev.draft,
        tasks: [...prev.draft.tasks, { type: 'acknowledgement', title: '', required: false }]
      }
    }));
  }
  function removeDraftTask(idx) {
    setTplDialog((prev) => ({
      ...prev,
      draft: { ...prev.draft, tasks: prev.draft.tasks.filter((_, i) => i !== idx) }
    }));
  }

  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
        <Typography variant="h4" fontWeight={600}>Pre-session workflow</Typography>
        <LinearProgress sx={{ mt: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: 'auto' }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1}>
          <Box>
            <Typography variant="h4" fontWeight={600}>Pre-session workflow</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              Define re-usable pre-session task bundles (assessments, reading, acknowledgements) and assign them to clients ahead of their next session.
              A soft-gate warns you if a client hasn&apos;t completed the required tasks; you can override it.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setInviteDialogOpen(true)}
          >
            Invite client
          </Button>
        </Stack>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        <Card variant="outlined">
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6">Templates ({templates.length})</Typography>
              <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={openCreate}>
                New template
              </Button>
            </Stack>

            {templates.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No templates yet. Create one — a typical first template is a PHQ-9 + GAD-7 with a brief consent acknowledgement.
              </Typography>
            ) : (
              <Stack divider={<Divider />} spacing={2}>
                {templates.map(tpl => (
                  <Box key={tpl._id}>
                    <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {tpl.name}
                          {tpl.gateSessionStart ? (
                            <Chip size="small" label="Soft gate" color="info" variant="outlined" sx={{ ml: 1 }} />
                          ) : (
                            <Chip size="small" label="No gate" sx={{ ml: 1 }} />
                          )}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {tpl.description || `${tpl.tasks?.length || 0} task${tpl.tasks?.length === 1 ? '' : 's'}`}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                          {(tpl.tasks || []).map((t, i) => (
                            <Chip key={t._id || i} size="small" label={`${t.title}${t.required ? ' *' : ''}`} variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" startIcon={<AssignmentIcon />} variant="contained" onClick={() => openAssign(tpl._id)}>
                          Assign to client
                        </Button>
                        <Button size="small" onClick={() => openEdit(tpl)}>Edit</Button>
                        <Tooltip title="Delete template">
                          <IconButton size="small" onClick={() => deleteTemplate(tpl._id)}><DeleteIcon /></IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>Open assignments by client</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click a client to load their open assignments and per-task completion state.
            </Typography>
            {clients.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No clients yet.</Typography>
            ) : (
              <List disablePadding>
                {clients.map((c) => {
                  const id = c._id || c.id;
                  const list = assignmentsByClient[id];
                  return (
                    <ListItem key={id} alignItems="flex-start" disableGutters
                      sx={{ flexDirection: 'column', borderBottom: '1px solid', borderColor: 'divider', py: 1 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}>
                        <ListItemText
                          primary={`${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email}
                          secondary={c.email}
                        />
                        <Button size="small" onClick={() => loadAssignmentsForClient(id)}>
                          {list ? 'Reload' : 'Show'}
                        </Button>
                      </Stack>
                      {list && (
                        <Box sx={{ width: '100%', pl: 2, mt: 1 }}>
                          {list.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">No assignments.</Typography>
                          ) : (
                            <Stack spacing={0.5}>
                              {list.map((a) => {
                                const completed = (a.taskState || []).filter(t => t.completedAt).length;
                                const total = (a.taskState || []).length;
                                return (
                                  <Stack key={a._id} direction="row" alignItems="center" spacing={1}>
                                    <Chip size="small" label={`${completed}/${total} complete`} color={completed === total ? 'success' : 'default'} />
                                    <Typography variant="body2">
                                      {a.template?.name || a.templateId} — due {a.dueAt ? new Date(a.dueAt).toLocaleString() : 'no due date'}
                                    </Typography>
                                  </Stack>
                                );
                              })}
                            </Stack>
                          )}
                        </Box>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* Create / edit template dialog */}
      <Dialog open={tplDialog.open} onClose={() => setTplDialog({ ...tplDialog, open: false })} fullWidth maxWidth="md">
        <DialogTitle>{tplDialog.id ? 'Edit template' : 'New pre-session template'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Template name"
              fullWidth
              value={tplDialog.draft.name}
              onChange={(e) => setTplDialog({ ...tplDialog, draft: { ...tplDialog.draft, name: e.target.value } })}
              required
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={2}
              value={tplDialog.draft.description}
              onChange={(e) => setTplDialog({ ...tplDialog, draft: { ...tplDialog.draft, description: e.target.value } })}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={tplDialog.draft.gateSessionStart}
                  onChange={(e) => setTplDialog({ ...tplDialog, draft: { ...tplDialog.draft, gateSessionStart: e.target.checked } })}
                />
              }
              label="Soft gate (warn but don't block if required tasks aren't done)"
            />
            <Divider />
            <Typography variant="subtitle1">Tasks</Typography>
            <Stack spacing={1.5}>
              {tplDialog.draft.tasks.map((t, idx) => (
                <Card variant="outlined" key={idx}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                      <TextField
                        label="Title" size="small" fullWidth
                        value={t.title || ''}
                        onChange={(e) => updateDraftTask(idx, { title: e.target.value })}
                      />
                      <TextField
                        label="Type" select size="small" sx={{ minWidth: 200 }}
                        value={t.type}
                        onChange={(e) => updateDraftTask(idx, { type: e.target.value })}
                      >
                        {TASK_TYPES.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </TextField>
                      {t.type === 'assessment' && (
                        <TextField
                          label="Assessment" select size="small" sx={{ minWidth: 160 }}
                          value={t.assessmentKey || 'phq9'}
                          onChange={(e) => updateDraftTask(idx, { assessmentKey: e.target.value, title: t.title || e.target.value.toUpperCase() })}
                        >
                          {assessmentKeys.map(k => (
                            <MenuItem key={k} value={k}>{assessmentLabels[k] || k}</MenuItem>
                          ))}
                        </TextField>
                      )}
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!t.required}
                            onChange={(e) => updateDraftTask(idx, { required: e.target.checked })}
                          />
                        }
                        label="Required"
                      />
                      <IconButton onClick={() => removeDraftTask(idx)} size="small"><DeleteIcon /></IconButton>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
              <Button startIcon={<AddIcon />} onClick={addDraftTask} size="small">Add task</Button>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTplDialog({ ...tplDialog, open: false })}>Cancel</Button>
          <Button variant="contained" onClick={saveTemplate} disabled={working || !tplDialog.draft.name}>
            {tplDialog.id ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign-to-client dialog */}
      <Dialog open={assignDialog.open} onClose={() => setAssignDialog({ ...assignDialog, open: false })} fullWidth maxWidth="sm">
        <DialogTitle>Assign template to client</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Client" select fullWidth
              value={assignDialog.clientId}
              onChange={(e) => setAssignDialog({ ...assignDialog, clientId: e.target.value })}
            >
              {clients.map(c => (
                <MenuItem key={c._id || c.id} value={c._id || c.id}>
                  {`${c.firstName || ''} ${c.lastName || ''}`.trim() || c.email}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Due (local time)"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={assignDialog.dueAt}
              onChange={(e) => setAssignDialog({ ...assignDialog, dueAt: e.target.value })}
              helperText="Leave blank for no due date."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ ...assignDialog, open: false })}>Cancel</Button>
          <Button variant="contained" onClick={submitAssignment} disabled={working || !assignDialog.clientId}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} message={toast} />

      <InviteClientDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        token={localStorage.getItem('token')}
        onCreated={() => setToast('Invitation link created — copy it to share with the client.')}
      />
    </Box>
  );
}
